import { Card } from "@/components/ui/card";
import { 
  DollarSign, 
  Users, 
  CreditCard, 
  TrendingUp, 
  TrendingDown, 
  FolderKanban, 
  Activity,
  Wallet
} from "lucide-react";
import { useCurrency } from "@/hooks/use-currency";

interface StatCardsProps {
  totalRevenue: number;
  activeSubscriptions: number;
  pendingSubscriptions: number;
  netProfit: number;
  activeProjects: number;
  totalClients: number;
  pendingRequests: number;
}

export const StatCards = ({
  totalRevenue,
  activeSubscriptions,
  pendingSubscriptions,
  netProfit,
  activeProjects,
  totalClients,
  pendingRequests,
}: StatCardsProps) => {
  const { formatAmount, currency } = useCurrency();
  
  return (
    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      <Card className="p-6 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background via-background/80 to-primary/5 border-primary/20 group animate-fade-in">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              Total Revenue
            </p>
            <p className="text-3xl font-bold text-primary">
              {formatAmount(totalRevenue, currency)}
            </p>
          </div>
          <div className="p-4 bg-primary/10 rounded-full transition-transform duration-300 group-hover:scale-110 group-hover:bg-primary/20">
            <DollarSign className="h-6 w-6 text-primary" />
          </div>
        </div>
        <div className="mt-4 flex items-center text-sm text-green-600 dark:text-green-400">
          <TrendingUp className="h-4 w-4 mr-1" />
          <span>12% from last month</span>
        </div>
      </Card>

      <Card className={`p-6 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background via-background/80 ${netProfit >= 0 ? 'to-green-500/5 border-green-500/20' : 'to-red-500/5 border-red-500/20'} group animate-fade-in delay-100`}>
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              Net Profit
            </p>
            <p className={`text-3xl font-bold ${netProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatAmount(netProfit, currency)}
            </p>
          </div>
          <div className={`p-4 rounded-full transition-transform duration-300 group-hover:scale-110 ${netProfit >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
            <Wallet className={`h-6 w-6 ${netProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} />
          </div>
        </div>
        <div className={`mt-4 flex items-center text-sm ${netProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {netProfit >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
          <span>{netProfit >= 0 ? '8.2% increase' : '2.1% decrease'}</span>
        </div>
      </Card>

      <Card className="p-6 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background via-background/80 to-blue-500/5 border-blue-500/20 group animate-fade-in delay-200">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              Active Projects
            </p>
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {activeProjects}
            </p>
          </div>
          <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full transition-transform duration-300 group-hover:scale-110">
            <FolderKanban className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
        <div className="mt-4 flex items-center text-sm text-blue-600 dark:text-blue-400">
          <TrendingUp className="h-4 w-4 mr-1" />
          <span>5.1% increase</span>
        </div>
      </Card>

      <Card className="p-6 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background via-background/80 to-purple-500/5 border-purple-500/20 group animate-fade-in delay-300">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              Total Clients
            </p>
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {totalClients}
            </p>
          </div>
          <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full transition-transform duration-300 group-hover:scale-110">
            <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
        </div>
        <div className="mt-4 flex items-center text-sm text-purple-600 dark:text-purple-400">
          <TrendingUp className="h-4 w-4 mr-1" />
          <span>3.2% increase</span>
        </div>
      </Card>

      <Card className="p-6 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background via-background/80 to-green-500/5 border-green-500/20 group animate-fade-in delay-100">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              Active Payments
            </p>
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
              {activeSubscriptions}
            </p>
          </div>
          <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full transition-transform duration-300 group-hover:scale-110">
            <CreditCard className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
        </div>
        <div className="mt-4 flex items-center text-sm text-green-600 dark:text-green-400">
          <TrendingUp className="h-4 w-4 mr-1" />
          <span>Completed</span>
        </div>
      </Card>

      <Card className="p-6 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background via-background/80 to-yellow-500/5 border-yellow-500/20 group animate-fade-in delay-200">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              Pending Payments
            </p>
            <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
              {pendingSubscriptions}
            </p>
          </div>
          <div className="p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-full transition-transform duration-300 group-hover:scale-110">
            <CreditCard className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
          </div>
        </div>
        <div className="mt-4 flex items-center text-sm text-yellow-600 dark:text-yellow-400">
          <Activity className="h-4 w-4 mr-1" />
          <span>Awaiting payment</span>
        </div>
      </Card>

      <Card className="p-6 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background via-background/80 to-orange-500/5 border-orange-500/20 group animate-fade-in delay-300">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              Service Requests
            </p>
            <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
              {pendingRequests}
            </p>
          </div>
          <div className="p-4 bg-orange-100 dark:bg-orange-900/30 rounded-full transition-transform duration-300 group-hover:scale-110">
            <Activity className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
        </div>
        <div className="mt-4 flex items-center text-sm text-orange-600 dark:text-orange-400">
          <Activity className="h-4 w-4 mr-1" />
          <span>Pending review</span>
        </div>
      </Card>
    </div>
  );
};